import { Helmet } from 'react-helmet-async';
import { useLanguage } from '@/hooks/useLanguage';
import { getLocalizedPagePath, isLocalizedSeoPage, pagePathMap, type SeoPage } from '@/lib/publicRoutes';

interface SEOHeadProps {
  page?: SeoPage;
}

const seoContent = {
  fr: {
    home: {
      title: "OuraTrust | Visibilité locale & réputation",
      description: "Pilotez avis, fiche Google Business Profile, visibilité locale et concurrents dans une plateforme claire pour établissements locaux.",
      breadcrumbName: "Accueil",
    },
    login: {
      title: "Connexion | OuraTrust",
      description: "Connectez-vous à votre espace OuraTrust pour gérer vos avis clients et votre e-réputation.",
      breadcrumbName: "Connexion",
    },
    signup: {
      title: "Inscription | OuraTrust - Essai Gratuit 14 jours",
      description: "Créez votre compte OuraTrust et commencez à centraliser vos avis dès aujourd'hui. Essai gratuit de 14 jours.",
      breadcrumbName: "Inscription",
    },
    privacy: {
      title: "Politique de Confidentialité | OuraTrust",
      description: "Découvrez comment OuraTrust protège vos données personnelles conformément au RGPD et à la Google API Services User Data Policy.",
      breadcrumbName: "Politique de Confidentialité",
    },
    terms: {
      title: "Conditions d'Utilisation | OuraTrust",
      description: "Consultez les conditions générales d'utilisation de la plateforme OuraTrust.",
      breadcrumbName: "Conditions d'Utilisation",
    },
    legal: {
      title: "Mentions Légales | OuraTrust",
      description: "Informations légales et mentions obligatoires du site OuraTrust – Apex Digital Flow LLC.",
      breadcrumbName: "Mentions Légales",
    },
    contact: {
      title: "Contact | OuraTrust",
      description: "Contactez l'équipe OuraTrust pour toute question sur notre solution de gestion des avis clients.",
      breadcrumbName: "Contact",
    },
    dashboard: {
      title: "Tableau de Bord | OuraTrust",
      description: "Gérez vos avis clients depuis votre tableau de bord OuraTrust.",
      breadcrumbName: "Tableau de Bord",
    },
    'how-it-works': {
      title: "Comment ça marche | OuraTrust – Centralisation d'Avis",
      description: "Découvrez comment OuraTrust centralise vos avis Google, Facebook, Yelp et TripAdvisor en 3 étapes simples.",
      breadcrumbName: "Comment ça marche",
    },
    pricing: {
      title: "Tarifs | OuraTrust – Plans Starter, Pro et Business",
      description: "Découvrez les tarifs OuraTrust : Starter à 49€/mois, Pro à 99€/mois, Business à 199€/mois. Essai gratuit 14 jours.",
      breadcrumbName: "Tarifs",
    },
    about: {
      title: "À Propos | OuraTrust – Apex Digital Flow LLC",
      description: "Découvrez OuraTrust, la solution de centralisation d'avis clients éditée par Apex Digital Flow LLC.",
      breadcrumbName: "À Propos",
    },
    'data-deletion': {
      title: "Suppression des Données | OuraTrust",
      description: "Demandez la suppression de vos données OuraTrust. Procédure conforme RGPD et CCPA.",
      breadcrumbName: "Suppression des Données",
    },
    blog: {
      title: "OuraTrust Academy | Blog Expert Réputation en Ligne",
      description: "Conseils d'experts pour gérer vos avis Google, booster votre réputation en ligne et attirer plus de clients.",
      breadcrumbName: "Blog",
    },
    'google-data-usage': {
      title: "Utilisation des Données Google | OuraTrust",
      description: "Découvrez comment OuraTrust utilise vos données Google conformément à la Google API Services User Data Policy (Limited Use).",
      breadcrumbName: "Utilisation des Données Google",
    },
  },
  en: {
    home: {
      title: "OuraTrust | Local visibility & reputation",
      description: "Manage reviews, Google Business Profile, local visibility and competitors in one clear platform for local businesses.",
      breadcrumbName: "Home",
    },
    login: {
      title: "Login | OuraTrust",
      description: "Sign in to your OuraTrust dashboard to manage customer reviews and your online reputation.",
      breadcrumbName: "Login",
    },
    signup: {
      title: "Sign Up | OuraTrust - 14-Day Free Trial",
      description: "Create your OuraTrust account and start centralizing your reviews today. 14-day free trial included.",
      breadcrumbName: "Sign Up",
    },
    privacy: {
      title: "Privacy Policy | OuraTrust",
      description: "Learn how OuraTrust protects your personal data in compliance with GDPR and Google API Services User Data Policy.",
      breadcrumbName: "Privacy Policy",
    },
    terms: {
      title: "Terms of Service | OuraTrust",
      description: "Read the terms and conditions for using the OuraTrust platform.",
      breadcrumbName: "Terms of Service",
    },
    legal: {
      title: "Legal Notice | OuraTrust",
      description: "Legal information and mandatory notices for the OuraTrust website – Apex Digital Flow LLC.",
      breadcrumbName: "Legal Notice",
    },
    contact: {
      title: "Contact | OuraTrust",
      description: "Contact the OuraTrust team for any questions about our customer review management solution.",
      breadcrumbName: "Contact",
    },
    dashboard: {
      title: "Dashboard | OuraTrust",
      description: "Manage your customer reviews from your OuraTrust dashboard.",
      breadcrumbName: "Dashboard",
    },
    'how-it-works': {
      title: "How It Works | OuraTrust – Review Centralization",
      description: "Discover how OuraTrust centralizes your Google, Facebook, Yelp and TripAdvisor reviews in 3 simple steps.",
      breadcrumbName: "How It Works",
    },
    pricing: {
      title: "Pricing | OuraTrust – Starter, Pro & Business Plans",
      description: "Discover OuraTrust pricing: Starter at €49/mo, Pro at €99/mo, Business at €199/mo. 14-day free trial.",
      breadcrumbName: "Pricing",
    },
    about: {
      title: "About | OuraTrust – Apex Digital Flow LLC",
      description: "Discover OuraTrust, the customer review centralization solution published by Apex Digital Flow LLC.",
      breadcrumbName: "About",
    },
    'data-deletion': {
      title: "Data Deletion | OuraTrust",
      description: "Request deletion of your OuraTrust data. GDPR and CCPA compliant procedure.",
      breadcrumbName: "Data Deletion",
    },
    blog: {
      title: "OuraTrust Academy | Expert Online Reputation Blog",
      description: "Expert tips to manage your Google reviews, boost your online reputation and attract more customers.",
      breadcrumbName: "Blog",
    },
    'google-data-usage': {
      title: "Google Data Usage | OuraTrust",
      description: "Learn how OuraTrust uses your Google data in compliance with the Google API Services User Data Policy (Limited Use).",
      breadcrumbName: "Google Data Usage",
    },
  }
};

function buildBreadcrumbJsonLd(page: SeoPage, baseUrl: string, language: 'fr' | 'en') {
  const content = seoContent[language][page as keyof typeof seoContent['fr']];
  if (!content) return null;

  const homeName = language === 'fr' ? 'Accueil' : 'Home';
  const items: Array<{ '@type': string; position: number; name: string; item?: string }> = [
    {
      '@type': 'ListItem',
      position: 1,
      name: homeName,
      item: `${baseUrl}${getLocalizedPagePath('home', language)}`,
    },
  ];

  if (page !== 'home') {
    items.push({
      '@type': 'ListItem',
      position: 2,
      name: content.breadcrumbName,
      item: `${baseUrl}${getLocalizedPagePath(page, language)}`,
    });
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items,
  };
}

export function SEOHead({ page = 'home' }: SEOHeadProps) {
  const { language } = useLanguage();
  const content = seoContent[language][page] || seoContent[language].home;
  const baseUrl = 'https://ouratrust.com';
  const canonicalPath = getLocalizedPagePath(page, language);
  const canonicalUrl = `${baseUrl}${canonicalPath}`;
  const frUrl = `${baseUrl}${isLocalizedSeoPage(page) ? getLocalizedPagePath(page, 'fr') : pagePathMap[page]}`;
  const enUrl = `${baseUrl}${isLocalizedSeoPage(page) ? getLocalizedPagePath(page, 'en') : pagePathMap[page]}`;
  const breadcrumbJsonLd = buildBreadcrumbJsonLd(page, baseUrl, language);

  return (
    <Helmet>
      <html lang={language} />
      <title>{content.title}</title>
      <meta name="description" content={content.description} />
      <link rel="canonical" href={canonicalUrl} />
      <link rel="alternate" hrefLang="fr" href={frUrl} />
      <link rel="alternate" hrefLang="en" href={enUrl} />
      <link rel="alternate" hrefLang="x-default" href={frUrl} />
      <meta property="og:title" content={content.title} />
      <meta property="og:description" content={content.description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content="OuraTrust" />
      <meta property="og:type" content="website" />
      <meta property="og:image" content={`${baseUrl}/og-image.png`} />
      <meta property="og:locale" content={language === 'fr' ? 'fr_FR' : 'en_US'} />
      <meta property="og:locale:alternate" content={language === 'fr' ? 'en_US' : 'fr_FR'} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={content.title} />
      <meta name="twitter:description" content={content.description} />
      <meta name="twitter:image" content={`${baseUrl}/og-image.png`} />
      {breadcrumbJsonLd ? (
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
      ) : null}
    </Helmet>
  );
}
